import React, { FC } from 'react'
import { Box, Typography, keyframes } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ScienceIcon from '@mui/icons-material/Science'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy'
import HistoryIcon from '@mui/icons-material/History'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'

const pulse = keyframes`
    0%, 100% { opacity: 1; }
    50% { opacity: 0.4; }
`

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`

const shimmer = keyframes`
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
`

const TOOL_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
    searchActives: { label: 'Buscando ativos', icon: <SearchIcon fontSize="inherit" />, color: '#7C4DFF' },
    getTopicalActiveDetails: { label: 'Detalhes do ativo tópico', icon: <ScienceIcon fontSize="inherit" />, color: '#7C4DFF' },
    getOralActiveDetails: { label: 'Detalhes do ativo oral', icon: <LocalPharmacyIcon fontSize="inherit" />, color: '#7C4DFF' },
    listPatientConsultations: { label: 'Consultando pacientes', icon: <PersonSearchIcon fontSize="inherit" />, color: '#2196F3' },
    getConsultationDetails: { label: 'Detalhes da consulta', icon: <HistoryIcon fontSize="inherit" />, color: '#2196F3' },
    listVehicles: { label: 'Buscando veículos', icon: <ScienceIcon fontSize="inherit" />, color: '#00BCD4' },
    checkCompatibility: { label: 'Verificando compatibilidade', icon: <CompareArrowsIcon fontSize="inherit" />, color: '#FF9800' },
    searchByCondition: { label: 'Buscando por condição', icon: <SearchIcon fontSize="inherit" />, color: '#7C4DFF' },
    suggestFormula: { label: 'Montando fórmula', icon: <AutoAwesomeIcon fontSize="inherit" />, color: '#E91E63' },
    getPatientHistory: { label: 'Histórico do paciente', icon: <PersonSearchIcon fontSize="inherit" />, color: '#2196F3' },
    duplicateConsultation: { label: 'Duplicando fórmula', icon: <ContentCopyIcon fontSize="inherit" />, color: '#4CAF50' },
    searchKnowledge: { label: 'Consultando base de conhecimento', icon: <MenuBookIcon fontSize="inherit" />, color: '#9C27B0' },
}

type ToolCallIndicatorProps = {
    tools: string[]
}

const ToolCallIndicator: FC<ToolCallIndicatorProps> = ({ tools }) => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, py: 1 }}>
            {tools.map((tool, idx) => {
                const config = TOOL_CONFIG[tool] || {
                    label: 'Processando',
                    icon: <AutoAwesomeIcon fontSize="inherit" />,
                    color: '#9E9E9E',
                }

                return (
                    <Box
                        key={`${tool}-${idx}`}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 2,
                            bgcolor: `${config.color}08`,
                            border: '1px solid',
                            borderColor: `${config.color}20`,
                            animation: `${pulse} 2s ease-in-out infinite`,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                bgcolor: `${config.color}15`,
                                color: config.color,
                                fontSize: 14,
                                animation: `${spin} 2s linear infinite`,
                            }}
                        >
                            {config.icon}
                        </Box>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 500,
                                color: 'text.secondary',
                                background: `linear-gradient(90deg, ${config.color}DD, ${config.color}44, ${config.color}DD)`,
                                backgroundSize: '200% 100%',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                animation: `${shimmer} 2s linear infinite`,
                            }}
                        >
                            {config.label}...
                        </Typography>
                    </Box>
                )
            })}
        </Box>
    )
}

export default ToolCallIndicator